<template>
  <!-- Interface Modale : Création & Édition de Fiche -->
  <div v-if="isModalOpen" class="fixed inset-0 flex items-center justify-center z-50 p-4 transition-all duration-300 backdrop-blur-sm"
    :class="isDark ? 'bg-black/60' : 'bg-zinc-900/40'">
    
    <div class="rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden text-left border transition-colors duration-200 font-sans"
      :class="isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'">

      <!-- Entête de la Modale -->
      <div class="flex justify-between items-center px-6 py-4 border-b transition-colors duration-200"
        :class="isDark ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-100 bg-zinc-50/80'">
        <div class="space-y-0.5">
          <h2 class="text-base font-semibold tracking-tight transition-colors" :class="isDark ? 'text-white' : 'text-zinc-900'">
            {{ isEditing ? "Édition de la Fiche Client" : "Créer un Nouveau Profil" }}
          </h2>
          <p class="text-[11px] font-medium transition-colors" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
            Historique confidentiel rattaché à la marque et à l'établissement.
          </p>
        </div>
        <button @click="closeModal" class="transition-colors text-xl focus:outline-none cursor-pointer"
          :class="isDark ? 'text-zinc-500 hover:text-white' : 'text-zinc-400 hover:text-zinc-900'">&times;</button>
      </div>

      <!-- Corps de la Modale -->
      <div class="p-6 space-y-4 text-xs overflow-y-auto flex-1 no-scrollbar transition-colors"
        :class="isDark ? 'bg-zinc-950/40' : 'bg-zinc-50/30'">

        <!-- Statistiques d'assiduité du client (En mode édition) -->
        <div v-if="isEditing" class="grid grid-cols-1 sm:grid-cols-4 gap-4 p-4 border rounded-xl transition-colors"
          :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">
          <div class="space-y-0.5">
            <p class="text-[9px] font-bold uppercase tracking-wider transition-colors" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Rendez-vous honorés</p>
            <p class="text-base font-semibold transition-colors" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ form.nombre_visites }}</p>
          </div>
          <div class="space-y-0.5">
            <p class="text-[9px] font-bold uppercase tracking-wider transition-colors" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Taux d'annulation</p>
            <p class="text-base font-semibold" :class="form.pourcentage_annulations > 15 ? 'text-amber-500' : (isDark ? 'text-zinc-400' : 'text-zinc-600')">
              {{ form.pourcentage_annulations }}%
            </p>
          </div>
          <div class="space-y-0.5">
            <p class="text-[9px] font-bold uppercase tracking-wider transition-colors" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Total réservations</p>
            <p class="text-base font-semibold transition-colors" :class="isDark ? 'text-white' : 'text-zinc-900'">
              {{ form.total_rdv }} <span class="text-[10px] font-medium" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">visites</span>
            </p>
          </div>
          <div class="space-y-0.5">
            <p class="text-[9px] font-bold uppercase tracking-wider transition-colors" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Absences ou désistements</p>
            <p class="text-base font-semibold" :class="form.nb_annulations > 0 ? 'text-rose-500' : (isDark ? 'text-zinc-400' : 'text-zinc-600')">
              {{ form.nb_annulations }} <span class="text-[10px] font-medium" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">manqué(s)</span>
            </p>
          </div>
        </div>

        <!-- Sections déroulantes d'informations -->
        <template v-for="(section, key) in modalSections" :key="key">
          <div class="border rounded-xl overflow-hidden transition-all duration-200"
            :class="isDark ? 'border-zinc-900 bg-zinc-900/40' : 'border-zinc-200/60 bg-white shadow-sm'">
            <button
              class="w-full flex justify-between items-center px-5 py-3.5 font-semibold transition-all text-xs uppercase tracking-wider border-b focus:outline-none cursor-pointer"
              :class="isDark 
                ? 'bg-zinc-900/80 hover:bg-zinc-900 text-zinc-300 hover:text-white border-zinc-900' 
                : 'bg-zinc-50 hover:bg-zinc-100/70 text-zinc-600 hover:text-zinc-900 border-zinc-200/60'"
              @click="openExclusive(key)"
            >
              {{ section.label }}
              <span class="text-[10px] font-normal transition-transform duration-300" :class="{ 'rotate-180': openSections[key], 'text-zinc-400': !openSections[key] }">▼</span>
            </button>

            <div v-show="openSections[key]" class="p-5 transition-colors" :class="isDark ? 'bg-zinc-950/30' : 'bg-zinc-50/30'">
              <component :is="section.component" v-model="form" :class="isDark ? 'text-zinc-300' : 'text-zinc-700'" />
            </div>
          </div>
        </template>
      </div>

      <!-- Pied de la Modale -->
      <div class="px-6 py-4 border-t flex justify-end gap-3 shrink-0 transition-colors duration-200"
        :class="isDark ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-100 bg-zinc-50/80'">
        <button @click="closeModal" class="px-4 py-2 rounded-xl text-xs font-semibold border transition-colors cursor-pointer focus:outline-none"
          :class="isDark 
            ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400 hover:text-white' 
            : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600 hover:text-zinc-900 shadow-sm'">
          Annuler
        </button>
        <button @click="submitClient" :disabled="isSubmitting" class="px-4 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus:outline-none shadow-sm"
          :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
          {{ isSubmitting ? 'Enregistrement...' : (isEditing ? "Enregistrer les modifications" : "Confirmer la création") }}
        </button>
      </div>
    </div>
  </div>

  <!-- Page Principale : Fichier Clients -->
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <!-- Entête de Page -->
    <div class="flex sm:flex-row flex-col justify-between items-start sm:items-center gap-4 border-b pb-5 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <h1 class="text-xl font-semibold tracking-tight transition-colors duration-200" :class="isDark ? 'text-white' : 'text-zinc-900'">Fichier Clients</h1>
        <p class="text-xs font-medium transition-colors duration-200" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          Registre de l'identité, historique des visites, assiduité et préférences de l'ensemble de votre clientèle.
        </p>
      </div>
      <button @click="openModal" class="px-4 py-2.5 rounded-xl text-xs font-semibold transition-all shadow-sm cursor-pointer focus:outline-none"
        :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
        Créer un profil client
      </button>
    </div>

    <!-- Chargement -->
    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <div class="text-xs font-medium animate-pulse" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Chargement des fiches clients...</div>
    </div>

    <!-- Tableau Premium des Clients -->
    <div v-else class="overflow-x-auto border rounded-2xl transition-all duration-200 no-scrollbar"
      :class="isDark ? 'bg-zinc-900/20 border-zinc-900 shadow-2xl shadow-black/20' : 'bg-white border-zinc-200/80 shadow-sm'">
      <table class="min-w-full divide-y text-left transition-colors" :class="isDark ? 'divide-zinc-900' : 'divide-zinc-100'">
        <thead class="text-[10px] uppercase tracking-wider font-semibold transition-colors"
          :class="isDark ? 'bg-zinc-950/80 text-zinc-500 border-b border-zinc-900' : 'bg-zinc-50/80 text-zinc-400 border-b border-zinc-200/60'">
          <tr>
            <th class="px-6 py-4">Nom & Identité</th>
            <th class="px-6 py-4">Coordonnées</th>
            <th class="px-6 py-4">Visites</th>
            <th class="px-6 py-4">Absences</th>
            <th class="px-6 py-4">% d'absence</th>
            <th class="px-6 py-4">Statut</th>
            <th class="px-6 py-4 text-center">Mesures spécifiques</th>
            <th class="px-6 py-4 text-right pr-8">Actions</th>
          </tr>
        </thead>

        <tbody class="divide-y bg-transparent text-xs transition-colors" :class="isDark ? 'divide-zinc-900/60' : 'divide-zinc-100'">
          <tr v-for="client in paginatedClients" :key="client.clientId" class="group transition-colors duration-150"
            :class="isDark ? 'hover:bg-zinc-900/30' : 'hover:bg-zinc-50/50'">

            <!-- Identité -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="font-medium tracking-wide flex items-center gap-2 transition-colors"
                :class="isDark ? 'text-white group-hover:text-zinc-300' : 'text-zinc-900 group-hover:text-zinc-800'">
                <span>{{ client.nom }} {{ client.prenom }}</span>
                <span v-if="client.blacklist" class="text-[8px] font-bold bg-rose-950/60 text-rose-400 border border-rose-900/40 px-1.5 py-0.5 rounded">
                  Bloqué
                </span>
              </div>
              <div class="text-[10px] mt-0.5 transition-colors" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">{{ client.email }}</div>
            </td>

            <!-- Contact -->
            <td class="px-6 py-4 whitespace-nowrap font-medium" :class="isDark ? 'text-zinc-400' : 'text-zinc-600'">
              {{ client.telephone || '—' }}
            </td>

            <!-- Visites -->
            <td class="px-6 py-4 whitespace-nowrap font-semibold" :class="isDark ? 'text-zinc-200' : 'text-zinc-900'">
              {{ client.nombre_visites }}
            </td>

            <!-- Absences -->
            <td class="px-6 py-4 whitespace-nowrap font-medium">
              <span :class="client.nb_annulations > 0 ? 'text-rose-500 font-semibold' : (isDark ? 'text-zinc-500' : 'text-zinc-400')">
                {{ client.nb_annulations }}
              </span>
            </td>

            <!-- Pourcentage d'absence -->
            <td class="px-6 py-4 whitespace-nowrap font-medium">
              <span :class="client.pourcentage_annulations > 15 ? 'text-amber-500 font-semibold' : (isDark ? 'text-zinc-500' : 'text-zinc-400')">
                {{ client.pourcentage_annulations }}%
              </span>
            </td>

            <!-- Statut de la fiche -->
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide border transition-colors" 
                :class="client.blacklist 
                  ? 'bg-rose-950/30 text-rose-400 border-rose-900/30' 
                  : 'bg-emerald-950/20 text-emerald-500 border-emerald-900/30'">
                {{ client.blacklist ? 'Bloqué' : 'Actif' }}
              </span>
            </td>

            <!-- Mesures de réservation -->
            <td class="px-6 py-4 text-center whitespace-nowrap">
              <div class="flex items-center justify-center">
                <span v-if="client.acompte_demande"
                  class="px-2 py-0.5 text-[9px] uppercase font-semibold rounded-md border bg-amber-950/20 text-amber-500 border-amber-900/20"
                  title="Acompte requis pour ce client">
                  Acompte exigé
                </span>
                <span v-else class="text-zinc-600">—</span>
              </div>
            </td>

            <!-- Actions sur la ligne -->
            <td class="px-6 py-4 whitespace-nowrap text-right pr-8">
              <div class="flex items-center justify-end gap-1.5">
                <!-- Voir détails (redirige vers /client/:id) -->
                <button @click="goToClientDetail(client.clientId)"
                  class="p-2 border rounded-xl transition-colors cursor-pointer focus:outline-none shadow-sm"
                  :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400 hover:text-white' : 'bg-white hover:bg-zinc-50 border-zinc-200 text-zinc-600'"
                  title="Consulter la fiche">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>

                <!-- Demander accompte (toggle) -->
                <button @click="requestDeposit(client)"
                  class="p-2 border rounded-xl transition-colors cursor-pointer focus:outline-none shadow-sm"
                  :class="client.acompte_demande
                    ? (isDark ? 'bg-amber-950/20 border-amber-900/40 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-700')
                    : (isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400 hover:text-white' : 'bg-white hover:bg-zinc-50 border-zinc-200 text-zinc-600')"
                  :title="client.acompte_demande ? 'Retirer la demande d\'accompte' : 'Demander un accompte'">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>

                <!-- Bloquer / Débloquer -->
                <button @click="toggleBlacklistStatus(client)"
                  class="p-2 border rounded-xl transition-colors cursor-pointer focus:outline-none shadow-sm"
                  :class="client.blacklist
                    ? (isDark ? 'bg-emerald-950/20 hover:bg-emerald-950/40 text-emerald-400 border-emerald-900/40' : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200')
                    : (isDark ? 'bg-rose-950/20 hover:bg-rose-950/40 text-rose-400 border-rose-900/40' : 'bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200')"
                  :title="client.blacklist ? 'Autoriser à nouveau' : 'Restreindre les réservations'">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </button>

                <!-- Supprimer -->
                <button @click="deleteClient(client.clientId)"
                  class="p-2 border rounded-xl transition-colors cursor-pointer focus:outline-none shadow-sm"
                  :class="isDark ? 'bg-zinc-900 hover:bg-rose-950/40 border-zinc-800 hover:border-rose-900/40 text-zinc-600 hover:text-rose-400' : 'bg-white hover:bg-rose-50 border-zinc-200 hover:border-rose-200 text-zinc-400 hover:text-rose-600'"
                  title="Supprimer la fiche">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </td>

          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination fluide -->
    <div v-if="!isLoading && totalPages > 0" class="flex justify-end items-center gap-1.5 pt-2">
      <button @click="prevPage" :disabled="currentPage === 1" 
        class="w-7 h-7 flex items-center justify-center border text-xs font-semibold rounded-full transition-colors disabled:opacity-30 disabled:pointer-events-none focus:outline-none cursor-pointer"
        :class="isDark ? 'border-zinc-900 bg-zinc-900/30 text-zinc-500 hover:text-white' : 'border-zinc-200 bg-white text-zinc-400 hover:text-zinc-900 shadow-sm'">
        ‹
      </button>
      <div class="flex gap-1">
        <button v-for="p in totalPages" :key="p" @click="goToPage(p)" 
          class="w-7 h-7 text-xs font-semibold rounded-full transition-all border focus:outline-none cursor-pointer" 
          :class="[p === currentPage 
            ? (isDark ? 'bg-white text-black border-white shadow-md' : 'bg-zinc-900 text-white border-zinc-900 shadow-sm') 
            : 'bg-transparent border-transparent text-zinc-400 hover:text-zinc-600']">
          {{ p }}
        </button>
      </div>
      <button @click="nextPage" :disabled="currentPage === totalPages" 
        class="w-7 h-7 flex items-center justify-center border text-xs font-semibold rounded-full transition-colors disabled:opacity-30 disabled:pointer-events-none focus:outline-none cursor-pointer"
        :class="isDark ? 'border-zinc-900 bg-zinc-900/30 text-zinc-500 hover:text-white' : 'border-zinc-200 bg-white text-zinc-400 hover:text-zinc-900 shadow-sm'">
        ›
      </button>
    </div>

  </div>
</template>
<style scoped>
.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
</style>

<script setup>
import { ref, reactive, computed, onMounted } from "vue"
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import api from '@/api'
import { useTheme } from '@/composables/useTheme';

const router = useRouter()
const route = useRoute()

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();
/* =========================
   ÉTATS RÉACTIFS
========================= */
const clients = ref([])
const isLoading = ref(true)
const isSubmitting = ref(false)
const isModalOpen = ref(false)
const isEditing = ref(false)
const selectedClientId = ref(null)

/* =========================
   FORMULAIRE CLIENT
========================= */
const form = reactive({
  nom: "",
  prenom: "",
  email: "",
  telephone: "",
  ville: "",
  date_naissance: "",
  genre: "",
  blacklist: false,
  acompte_demande: false,
  notes: "",
  // Stats (lecture seule)
  nombre_visites: 0,
  nb_annulations: 0,
  total_rdv: 0,
  pourcentage_annulations: 0
})

/* =========================
   SECTIONS DU MODAL
========================= */
const modalSections = {
  infos: {
    label: "Identité & Coordonnées de Contact",
    component: {
      props: ["modelValue"],
      template: `
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 col-span-2 w-full">
          <div class="space-y-1">
            <label class="text-[9px] uppercase tracking-wider font-mono text-zinc-500">Nom</label>
            <input v-model="modelValue.nom" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white focus:outline-none focus:border-zinc-700 text-xs font-light" />
          </div>
          <div class="space-y-1">
            <label class="text-[9px] uppercase tracking-wider font-mono text-zinc-500">Prénom</label>
            <input v-model="modelValue.prenom" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white focus:outline-none focus:border-zinc-700 text-xs font-light" />
          </div>
          <div class="space-y-1">
            <label class="text-[9px] uppercase tracking-wider font-mono text-zinc-500">Adresse Email</label>
            <input v-model="modelValue.email" type="email" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white focus:outline-none focus:border-zinc-700 text-xs font-light" />
          </div>
          <div class="space-y-1">
            <label class="text-[9px] uppercase tracking-wider font-mono text-zinc-500">Téléphone Portatif</label>
            <input v-model="modelValue.telephone" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white focus:outline-none focus:border-zinc-700 text-xs font-light" />
          </div>
          <div class="space-y-1">
            <label class="text-[9px] uppercase tracking-wider font-mono text-zinc-500">Ville</label>
            <input v-model="modelValue.ville" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white focus:outline-none focus:border-zinc-700 text-xs font-light" />
          </div>
          <div class="space-y-1">
            <label class="text-[9px] uppercase tracking-wider font-mono text-zinc-500">Date de naissance</label>
            <input v-model="modelValue.date_naissance" type="date" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white focus:outline-none focus:border-zinc-700 text-xs font-light" />
          </div>
          <div class="space-y-1">
            <label class="text-[9px] uppercase tracking-wider font-mono text-zinc-500">Genre</label>
            <select v-model="modelValue.genre" class="w-full px-4 py-2 rounded-xl border border-zinc-800 bg-zinc-950 text-white focus:outline-none focus:border-zinc-700 text-xs font-light">
              <option value="">Non spécifié</option>
              <option value="Homme">Homme</option>
              <option value="Femme">Femme</option>
              <option value="Autre">Autre</option>
            </select>
          </div>
        </div>
      `
    }
  },
  options: {
    label: "Garde-Fous, Acomptes & Restrictions (Anti-Churn)",
    component: {
      props: ["modelValue"],
      template: `
        <div class="space-y-5 col-span-2 w-full text-left">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="p-4 bg-zinc-950 rounded-xl border border-zinc-850 flex items-center justify-between">
              <div class="space-y-0.5">
                <p class="text-xs font-medium text-white">Statut du Compte</p>
                <p class="text-[10px] text-zinc-500 font-light">Inscrire ou lever la sanction administrative.</p>
              </div>
              <input type="checkbox" v-model="modelValue.blacklist" class="h-4 w-4 rounded accent-red-500 border-zinc-800 bg-zinc-900" />
            </div>

            <div class="p-4 bg-zinc-950 rounded-xl border border-zinc-850 flex items-center justify-between">
              <div class="space-y-0.5">
                <p class="text-xs font-medium text-white">Exiger un Acompte Bancaire</p>
                <p class="text-[10px] text-zinc-500 font-light">Force la prise d'empreinte CB pour ce profil.</p>
              </div>
              <input type="checkbox" v-model="modelValue.acompte_demande" class="h-4 w-4 rounded accent-zinc-200 border-zinc-800 bg-zinc-900" />
            </div>
          </div>
        </div>
      `
    }
  },
  notes: {
    label: "Registre des Notes Internes de Sécurité",
    component: {
      props: ["modelValue"],
      template: `
        <div class="w-full col-span-2 space-y-1">
          <label class="text-[9px] uppercase tracking-wider font-mono text-zinc-500">Notes Secrètes de l'Équipe</label>
          <textarea
            v-model="modelValue.notes"
            rows="3"
            placeholder="Historique des altercations ou consignes particulières sur ce client..."
            class="w-full p-4 rounded-xl border border-zinc-800 bg-zinc-950 text-white placeholder-zinc-700 focus:outline-none text-xs font-light resize-none"
          ></textarea>
        </div>
      `
    }
  }
}

const openSections = reactive({ infos: true, options: false, notes: false })
const openExclusive = (key) => {
  Object.keys(openSections).forEach(k => openSections[k] = false)
  openSections[key] = true
}

/* =========================
   RÉCUPÉRER LES CLIENTS DEPUIS L'API
========================= */
const fetchClients = async () => {
  try {
    isLoading.value = true
    const response = await api.clients.getAllClients()
    clients.value = response.data || []
  } catch (error) {
    console.error('Erreur lors du chargement des clients:', error)
    toast.error('Erreur lors du chargement des clients', {
      position: 'top-right',
      autoClose: 3000
    })
  } finally {
    isLoading.value = false
  }
}

/* =========================
   OUVRIR/FERMER MODAL
========================= */
const openModal = () => {
  isEditing.value = false
  selectedClientId.value = null
  Object.assign(form, {
    nom: "", prenom: "", email: "", telephone: "", ville: "",
    date_naissance: "", genre: "", blacklist: false, acompte_demande: false, notes: "",
    nombre_visites: 0, nb_annulations: 0, total_rdv: 0, pourcentage_annulations: 0
  })
  isModalOpen.value = true
  openSections.infos = true
  openSections.options = false
  openSections.notes = false
}

const openEditModal = (client) => {
  isEditing.value = true
  selectedClientId.value = client.clientId || client.id
  Object.assign(form, {
    nom: client.nom || "",
    prenom: client.prenom || "",
    email: client.email || "",
    telephone: client.telephone || "",
    ville: client.ville || "",
    date_naissance: client.date_naissance || "",
    genre: client.genre || "",
    blacklist: client.blacklist || false,
    acompte_demande: client.acompte_demande || false,
    notes: client.notes || "",
    nombre_visites: client.nombre_visites || 0,
    nb_annulations: client.nb_annulations || 0,
    total_rdv: client.total_rdv || 0,
    pourcentage_annulations: client.pourcentage_annulations || 0
  })
  isModalOpen.value = true
  openSections.infos = true
  openSections.options = false
  openSections.notes = false
}

const closeModal = () => {
  isModalOpen.value = false
}

/* =========================
   CRÉER OU MODIFIER UN CLIENT
========================= */
const submitClient = async () => {
  if (isSubmitting.value) return

  // Validation
  if (!form.nom || !form.email) {
    toast.error('Veuillez remplir au moins le nom et l\'email', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  try {
    isSubmitting.value = true

    const clientData = {
      nom: form.nom,
      prenom: form.prenom,
      email: form.email,
      telephone: form.telephone,
      ville: form.ville,
      date_naissance: form.date_naissance,
      genre: form.genre,
      blacklist: form.blacklist,
      acompte_demande: form.acompte_demande,
      notes: form.notes
    }

    if (isEditing.value) {
      // Modification
      await api.clients.updateClient(selectedClientId.value, {
        blacklist: form.blacklist,
        acompte_demande: form.acompte_demande
      })
      toast.success('Client modifié avec succès !', {
        position: 'top-right',
        autoClose: 2000
      })
    } else {
      // Création
      await api.clients.createClient(clientData)
      toast.success('Client créé avec succès !', {
        position: 'top-right',
        autoClose: 2000
      })
    }

    await fetchClients()
    closeModal()

  } catch (error) {
    console.error('Erreur lors de l\'enregistrement:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors de l\'enregistrement du client',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  } finally {
    isSubmitting.value = false
  }
}

/* =========================
   SUPPRIMER UN CLIENT
========================= */
const deleteClient = async (clientId) => {
  if (!confirm('Voulez-vous vraiment retirer ce client de l\'établissement ?')) {
    return
  }

  try {
    await api.clients.deleteClient(clientId)
    toast.success('Client retiré avec succès', {
      position: 'top-right',
      autoClose: 2000
    })
    await fetchClients()
  } catch (error) {
    console.error('Erreur lors de la suppression:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors de la suppression du client',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  }
}

/* =========================
   BLACKLISTER / RÉHABILITER
========================= */
const toggleBlacklistStatus = async (client) => {
  const newStatus = !client.blacklist
  const action = newStatus ? 'blacklister' : 'réhabiliter'

  if (!confirm(`Êtes-vous sûr de vouloir ${action} ce client ?`)) {
    return
  }

  try {
    await api.clients.updateClient(client.clientId, {
      blacklist: newStatus,
      acompte_demande: client.acompte_demande
    })

    toast.success(
      `Client ${newStatus ? 'blacklisté' : 'réhabilité'} avec succès`,
      {
        position: 'top-right',
        autoClose: 2000
      }
    )

    await fetchClients()
  } catch (error) {
    console.error('Erreur lors du changement de statut:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors du changement de statut',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  }
}

/* =========================
   REDIRECTION VERS DÉTAILS CLIENT
========================= */
const goToClientDetail = (clientId) => {
  router.push(`/clients/${clientId}`)
}

/* =========================
   DEMANDER UN ACCOMPTE (TOGGLE)
========================= */
const requestDeposit = async (client) => {
  const newStatus = !client.acompte_demande
  const actionText = newStatus ? 'demander un accompte' : 'retirer la demande d\'accompte'

  const confirmed = confirm(`Voulez-vous ${actionText} pour les futures réservations de ${client.prenom} ${client.nom} ?`)
  if (!confirmed) return

  try {
    await api.clients.updateClient(client.clientId, {
      blacklist: client.blacklist,
      acompte_demande: newStatus
    })

    toast.success(newStatus ? 'Accompte requis pour ce client' : 'Demande d\'accompte retirée', {
      position: 'top-right',
      autoClose: 2000
    })

    await fetchClients()
  } catch (error) {
    console.error('Erreur lors de la mise à jour:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors de la mise à jour de l\'accompte',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  }
}

/* =========================
   PAGINATION
========================= */
const currentPage = ref(1)
const pageSize = 8
const totalPages = computed(() => Math.ceil(clients.value.length / pageSize))
const paginatedClients = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return clients.value.slice(start, start + pageSize)
})
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }
const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
const goToPage = (p) => { currentPage.value = p }

/* =========================
   LIFECYCLE
========================= */
onMounted(async () => {
  await fetchClients()

  // Si un query param 'edit' est présent, ouvrir le modal d'édition pour ce client
  if (route.query.edit) {
    const clientToEdit = clients.value.find(c => c.id === route.query.edit)
    if (clientToEdit) {
      openEditModal(clientToEdit)
      // Nettoyer le query param
      router.replace({ query: {} })
    }
  }
})
</script>
